"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import posthog from "posthog-js";
import { type ReactNode, useState } from "react";

import {
  ONBOARDING_CARD_CLASS,
  ONBOARDING_ROW_CLASS,
  ONBOARDING_SHELL_CLASS,
} from "@/components/onboarding/onboarding-layout";
import { TaskBoardProductPreview } from "@/components/onboarding/task-board-product-preview";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { APP_NAME, BOOK_DEMO_URL } from "@/lib/app/brand";
import {
  emailDomain,
  PRODUCT_EVENTS,
  type AuthMethod,
} from "@/lib/observability/product-analytics";
import { createClient } from "@/lib/supabase/client";

type Status = "idle" | "sending" | "sent" | "error";

type AuthStepProps = {
  /** When true, auth actions are simulated locally and never touch Supabase. */
  preview?: boolean;
  initialError?: string | null;
  initialShowInviteModal?: boolean;
};

export function AuthStep({
  preview = false,
  initialError = null,
  initialShowInviteModal = false,
}: AuthStepProps) {
  const supabase = preview ? null : createClient();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(initialError);
  const [showInviteModal, setShowInviteModal] = useState(
    initialShowInviteModal,
  );

  async function sendMagicLink() {
    const method: AuthMethod = "magic_link";
    setStatus("sending");
    setError(null);
    posthog.capture(PRODUCT_EVENTS.authMagicLinkRequested, {
      auth_method: method,
      auth_surface: "login",
      email_domain: emailDomain(email),
    });

    if (preview) {
      await new Promise((resolve) => setTimeout(resolve, 400));
      setStatus("sent");
      return;
    }

    const { error } = await supabase!.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${location.origin}/auth/callback`,
        shouldCreateUser: true,
      },
    });

    if (error) {
      setError("We could not send a sign-in link. Try again.");
      setStatus("error");
      posthog.capture(PRODUCT_EVENTS.authMagicLinkFailed, {
        auth_method: method,
        auth_surface: "login",
        email_domain: emailDomain(email),
        error_message: error.message,
      });
      posthog.captureException(error, {
        auth_method: method,
        auth_surface: "login",
      });
    } else {
      posthog.capture(PRODUCT_EVENTS.authMagicLinkSent, {
        auth_method: method,
        auth_surface: "login",
        email_domain: emailDomain(email),
      });
      setStatus("sent");
    }
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    await sendMagicLink();
  }

  function handleGoBack() {
    setStatus("idle");
    setError(null);
  }

  async function handleGoogle() {
    if (preview) return;

    const method: AuthMethod = "google";
    posthog.capture(PRODUCT_EVENTS.authOauthStarted, {
      auth_method: method,
      auth_surface: "login",
    });
    const { error } = await supabase!.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${location.origin}/auth/callback` },
    });
    if (error) {
      setError(error.message);
      posthog.capture(PRODUCT_EVENTS.authOauthFailed, {
        auth_method: method,
        auth_surface: "login",
        error_message: error.message,
      });
      posthog.captureException(error, {
        auth_method: method,
        auth_surface: "login",
      });
    }
  }

  const submitDisabled = status === "sending" || status === "sent" || !email;

  if (status === "sent") {
    return (
      <AuthShell>
        <div className="flex flex-col items-center gap-12 px-6 text-center">
          <div className="flex flex-col items-center gap-3">
            <h1 className="text-[28px] leading-[1.4] font-medium tracking-[-0.56px] text-neutral-11">
              Check your email
            </h1>
            <p className="w-[412px] max-w-full font-body-m-regular text-[#868686]">
              We sent a sign-in link to {email}
            </p>
          </div>

          <div className="flex w-[296px] max-w-full flex-col items-center gap-4">
            <button
              type="button"
              onClick={sendMagicLink}
              className="flex w-full items-center justify-center rounded-full border border-[#eee] bg-white px-8 py-3 shadow-1 transition-colors hover:bg-neutral-02"
            >
              <span className="font-body-m-medium text-[#111]">
                Resend link
              </span>
            </button>

            <button
              type="button"
              onClick={handleGoBack}
              className="flex w-full items-center justify-center gap-2 rounded-full px-8 py-3 transition-colors hover:bg-neutral-02"
            >
              <ArrowLeft className="size-4 text-[#868686]" strokeWidth={2} />
              <span className="font-body-s-medium text-[#868686]">Go back</span>
            </button>

            {error && (
              <p className="font-caption-2-medium text-danger-9">{error}</p>
            )}
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="flex flex-col items-center gap-12 px-6 text-center">
        <div className="flex flex-col items-center gap-3">
          <h1 className="text-[28px] font-medium leading-[1.4] tracking-[-0.56px] text-neutral-11">
            {APP_NAME}
          </h1>
          <p className="w-[412px] max-w-full font-body-m-regular text-[#868686]">
            Grow your revenue with agents
          </p>
        </div>

        <div className="flex w-[296px] max-w-full flex-col items-center gap-4">
          <button
            type="button"
            onClick={handleGoogle}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-[#eee] bg-white px-8 py-3 shadow-1 transition-colors hover:bg-neutral-02"
          >
            <GoogleIcon />
            <span className="font-body-m-medium text-[#111]">
              Continue with Google
            </span>
          </button>

          <div className="flex w-full items-center justify-center gap-4">
            <div className="h-px flex-1 bg-[#eee]" />
            <span className="font-caption-2-medium text-[#999]">OR</span>
            <div className="h-px flex-1 bg-[#eee]" />
          </div>

          <form
            onSubmit={handleMagicLink}
            className="flex w-full items-center gap-2 rounded-full border border-[#eee] bg-white px-5 py-3 shadow-1 focus-within:border-neutral-05"
          >
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={status === "sending"}
              className="min-w-0 flex-1 bg-transparent font-body-m-regular text-neutral-11 placeholder:text-[#999] outline-none disabled:opacity-60 [&:-webkit-autofill]:[-webkit-text-fill-color:#1a1a1a] [&:-webkit-autofill]:[box-shadow:0_0_0px_1000px_white_inset] [&:-webkit-autofill]:[transition:background-color_9999s_ease-in-out_0s]"
            />
            <button
              type="submit"
              disabled={submitDisabled}
              aria-label="Send magic link"
              className={`flex size-6 shrink-0 items-center justify-center rounded-full transition-colors ${
                email
                  ? "bg-neutral-11 text-white hover:bg-neutral-10"
                  : "bg-[#F2F2F2] text-[#BBBBBB]"
              } `}
            >
              <ArrowRight className="size-4" strokeWidth={2} />
            </button>
          </form>

          {error && (
            <p className="font-caption-2-medium text-danger-9">{error}</p>
          )}
        </div>
      </div>

      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-md gap-10">
          <DialogHeader>
            <DialogTitle>You need an invite</DialogTitle>
            <DialogDescription>
              {APP_NAME} is invite-only right now. Book a quick demo and we’ll
              get you set up with access.
            </DialogDescription>
          </DialogHeader>
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              if (!preview) {
                posthog.capture(PRODUCT_EVENTS.authInviteRequiredCtaClicked, {
                  auth_surface: "login",
                });
              }
            }}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-neutral-11 px-8 py-3 text-white transition-colors hover:bg-neutral-10"
          >
            <span className="font-body-m-medium">Book a demo call</span>
            <ArrowRight className="size-4" strokeWidth={2} />
          </a>
        </DialogContent>
      </Dialog>
    </AuthShell>
  );
}

function AuthShell({ children }: { children: ReactNode }) {
  return (
    <main className={ONBOARDING_SHELL_CLASS}>
      <div className={ONBOARDING_ROW_CLASS}>
        <div className={ONBOARDING_CARD_CLASS}>
          <div className="flex min-h-0 flex-1 items-center justify-center">
            {children}
          </div>
        </div>
        <div className={`${ONBOARDING_CARD_CLASS} hidden lg:flex`}>
          <TaskBoardProductPreview />
        </div>
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M17.64 9.20455c0-.63818-.05727-1.25182-.16364-1.84091H9v3.48136h4.84364c-.20864 1.125-.84273 2.07818-1.79727 2.71636v2.25818h2.90727c1.70181-1.56636 2.68636-3.87409 2.68636-6.61499z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.46727-.80591 5.95636-2.18045l-2.90727-2.25818c-.80591.54-1.83682.85909-3.04909.85909-2.34409 0-4.32818-1.58318-5.03591-3.71045H.95682v2.33182C2.43818 15.9832 5.48182 18 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.96409 10.71c-.18-.54-.28227-1.11682-.28227-1.71s.10227-1.17.28227-1.71V4.95818H.95682C.34727 6.17318 0 7.54773 0 9s.34727 2.82682.95682 4.04182l3.00727-2.33182z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.57955c1.32136 0 2.50773.45409 3.44045 1.34591l2.58136-2.58136C13.46318.89182 11.4259 0 9 0 5.48182 0 2.43818 2.01682.95682 4.95818l3.00727 2.33182C4.67182 5.16273 6.65591 3.57955 9 3.57955z"
        fill="#EA4335"
      />
    </svg>
  );
}
